import { Box, Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import employeeImage from "../../assets/images/employee_image.png";
import { formatDateDDMMMYYYY } from "../reports/ColumnUtils";

const EMPTY = "-";

const labelSx = {
  fontSize: { xs: "0.72rem", md: "0.78rem" },
  color: "#888888",
};

const valueSx = {
  fontSize: { xs: "0.8rem", md: "0.85rem" },
  fontWeight: 500,
  color: "#474747",
  wordBreak: "break-word",
};

const sectionTitleSx = {
  fontSize: { xs: "0.9rem", md: "1rem" },
  fontWeight: 600,
  color: "#DEA829",
  mb: 1.5,
};

const emptySx = {
  fontSize: { xs: "0.78rem", md: "0.82rem" },
  color: "#888888",
};

const resolve = (options, id) =>
  options?.find((option) => String(option.value) === String(id))?.label;

const Field = ({ label, value, sm = 6, md = 4 }) => (
  <Grid item xs={12} sm={sm} md={md}>
    <Typography sx={labelSx}>{label}</Typography>
    <Typography sx={valueSx}>
      {value === 0 || value ? value : EMPTY}
    </Typography>
  </Grid>
);

const Section = ({ title, children }) => (
  <Box sx={{ mb: 3 }}>
    <Typography sx={sectionTitleSx}>{title}</Typography>
    <Grid container spacing={2}>
      {children}
    </Grid>
  </Box>
);

const EmployeeOverview = ({ employee, lookups = {} }) => {
  const { t } = useTranslation();
  const p = (key) => t(`pages.employee.personal_info.${key}`);

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "center", sm: "flex-start" },
          gap: { xs: 2, sm: 3 },
          mb: 3,
        }}
      >
        <Box
          sx={{
            width: { xs: "45vw", sm: "150px" },
            height: { xs: "45vw", sm: "150px" },
            minWidth: { sm: "150px" },
            border: "1px solid #FFE097",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          <img
            src={employee?.employee_photo || employeeImage}
            onError={(event) => {
              event.currentTarget.src = employeeImage;
            }}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </Box>

        <Box sx={{ flex: 1, width: "100%" }}>
          <Section title={t("pages.employee.overview.basic_information")}>
            <Field label={p("name")} value={employee?.name} sm={6} md={6} />
            <Field
              label={p("father_name")}
              value={employee?.father_name}
              sm={6}
              md={6}
            />
            <Field
              label={p("mobile_number")}
              value={employee?.mobile}
              sm={6}
              md={6}
            />
            <Field
              label={p("alternative_mobile")}
              value={employee?.alternate_mobile}
              sm={6}
              md={6}
            />
            <Field label={p("email")} value={employee?.email} sm={6} md={6} />
            <Field
              label={p("dob")}
              value={employee?.dob ? formatDateDDMMMYYYY(employee.dob) : null}
              sm={6}
              md={6}
            />
            <Field label={p("gender")} value={employee?.gender} sm={6} md={6} />
          </Section>
        </Box>
      </Box>

      <Section title={t("pages.employee.overview.personal_information")}>
        <Field
          label={p("marital_status")}
          value={resolve(lookups.maritalStatus, employee?.marital_status_id)}
        />
        <Field
          label={p("country")}
          value={resolve(lookups.countries, employee?.country_id)}
        />
        <Field
          label={p("state")}
          value={resolve(lookups.states, employee?.state_id)}
        />
        <Field
          label={p("city")}
          value={resolve(lookups.cities, employee?.city_id)}
        />
        <Field
          label={p("permanent_address")}
          value={employee?.permanent_address}
          sm={12}
          md={6}
        />
        <Field
          label={p("present_address")}
          value={employee?.present_address}
          sm={12}
          md={6}
        />
      </Section>
    </Box>
  );
};

export const EmployeeProfessional = ({ employee, lookups = {} }) => {
  const { t } = useTranslation();
  const p = (key) => t(`pages.employee.personal_info.${key}`);
  const pr = (key) => t(`pages.employee.professional_info.${key}`);

  const educations = employee?.education_info ?? [];
  const experiences = employee?.professional_info ?? [];

  return (
    <Box sx={{ width: "100%" }}>
      <Section title={t("pages.employee.overview.employee_information")}>
        <Field
          label={p("designation")}
          value={resolve(lookups.designations, employee?.designation_id)}
        />
        <Field
          label={p("department")}
          value={resolve(lookups.departments, employee?.department_id)}
        />
        <Field
          label={p("employment_type")}
          value={resolve(lookups.workTypes, employee?.employment_type_id)}
        />
        <Field
          label={p("date_of_joining")}
          value={
            employee?.date_of_joining
              ? formatDateDDMMMYYYY(employee.date_of_joining)
              : null
          }
        />
      </Section>

      <Box sx={{ mb: 3 }}>
        <Typography sx={sectionTitleSx}>{pr("education_title")}</Typography>
        {educations.length ? (
          educations.map((education, index) => (
            <Grid
              container
              spacing={2}
              key={`${education.institute_name || "education"}_${index}`}
              sx={{
                mb: 1.5,
                pb: 1.5,
                borderBottom:
                  index === educations.length - 1
                    ? "none"
                    : "1px solid #FFE097",
              }}
            >
              <Field
                label={pr("institution_name")}
                value={education.institute_name}
              />
              <Field
                label={pr("education_level")}
                value={resolve(
                  lookups.educationLevels,
                  education.education_level_id
                )}
              />
              <Field
                label={pr("start_date")}
                value={
                  education.start_date
                    ? formatDateDDMMMYYYY(education.start_date)
                    : null
                }
                md={2}
              />
              <Field
                label={pr("end_date")}
                value={
                  education.end_date
                    ? formatDateDDMMMYYYY(education.end_date)
                    : null
                }
                md={2}
              />
            </Grid>
          ))
        ) : (
          <Typography sx={emptySx}>
            {t("pages.employee.view.no_education")}
          </Typography>
        )}
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography sx={sectionTitleSx}>{pr("professional_title")}</Typography>
        {experiences.length ? (
          experiences.map((experience, index) => (
            <Grid
              container
              spacing={2}
              key={`${experience.company_name || "experience"}_${index}`}
              sx={{
                mb: 1.5,
                pb: 1.5,
                borderBottom:
                  index === experiences.length - 1
                    ? "none"
                    : "1px solid #FFE097",
              }}
            >
              <Field
                label={pr("company_name")}
                value={experience.company_name}
              />
              <Field
                label={pr("designation")}
                value={experience.designation}
              />
              <Field
                label={pr("start_date")}
                value={
                  experience.start_date
                    ? formatDateDDMMMYYYY(experience.start_date)
                    : null
                }
                md={2}
              />
              <Field
                label={pr("end_date")}
                value={
                  experience.end_date
                    ? formatDateDDMMMYYYY(experience.end_date)
                    : null
                }
                md={2}
              />
            </Grid>
          ))
        ) : (
          <Typography sx={emptySx}>
            {t("pages.employee.view.no_experience")}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default EmployeeOverview;
